/* eslint-disable no-underscore-dangle */
import picoxiaDicom from 'app/native/node-addons/picoxia-dicom';
import makeChainablePromise from 'app/utils/async/ChainablePromise';

export default class PACSCommunication {
  constructor(pacsConfiguration) {
    this.storePromiseChain = makeChainablePromise();
    this.pacsConfiguration_ = pacsConfiguration;
  }

  associate = (associationParameters) =>
    this.storePromiseChain.chainPromise(() => {
      const pacsCommunication = new (picoxiaDicom().PACSCommunication)();

      const association = pacsCommunication.associate(associationParameters);

      association
        .then((result) => {
          this.pacsConfiguration_ = associationParameters;
          return result;
        })
        .finally(() => pacsCommunication.release());

      return association;
    });

  echo = () =>
    this.storePromiseChain.chainPromise(() => {
      const pacsCommunication = new (picoxiaDicom().PACSCommunication)(this.pacsConfiguration_);

      return pacsCommunication.echo().finally(() => pacsCommunication.release());
    });

  store = (dataSet, options = {}) =>
    this.storePromiseChain.chainPromise(async () => {
      const pacsCommunication = new (picoxiaDicom().PACSCommunication)(this.pacsConfiguration_);

      const { signal } = options;

      let abortHandler;
      if (signal) {
        abortHandler = () => {
          pacsCommunication.abort();
        };

        if (signal.aborted) {
          pacsCommunication.abort();
          console.warn('PACSCommunication.store aborted before start');
          throw new DOMException('Operation cancelled', 'AbortError');
        }

        signal.addEventListener('abort', abortHandler);
      }

      try {
        return await pacsCommunication.store(dataSet);
      } finally {
        if (signal && abortHandler) {
          signal.removeEventListener('abort', abortHandler);
        }
        pacsCommunication.release();
      }
    });

  find = (query, queryModel) =>
    this.storePromiseChain.chainPromise(async () => {
      const pacsCommunication = new (picoxiaDicom().PACSCommunication)(this.pacsConfiguration_);

      return pacsCommunication.find(query, queryModel).finally(() => pacsCommunication.release());
    });

  get = (query, queryModel) =>
    this.storePromiseChain.chainPromise(async () => {
      const pacsCommunication = new (picoxiaDicom().PACSCommunication)(this.pacsConfiguration_);

      return pacsCommunication.get(query, queryModel).finally(() => pacsCommunication.release());
    });

  storeMany = (dataSets, options = {}) => {
    const pacsCommunication = new (picoxiaDicom().PACSCommunication)(this.pacsConfiguration_);

    const { signal } = options;
    let abortHandler;

    if (signal) {
      abortHandler = () => {
        pacsCommunication.abort();
      };

      if (signal.aborted) {
        pacsCommunication.abort();
        return Promise.reject(new DOMException('Operation cancelled', 'AbortError'));
      }

      signal.addEventListener('abort', abortHandler);
    }

    const storeRequests = dataSets.map((dataSet) =>
      this.storePromiseChain.chainPromise(() => pacsCommunication.store(dataSet))
    );

    Promise.allSettled(storeRequests).finally(() => {
      if (signal && abortHandler) {
        signal.removeEventListener('abort', abortHandler);
      }
      pacsCommunication.release();
    });

    return storeRequests;
  };

  checkAssociation = (pacsConfiguration) =>
    this.storePromiseChain.chainPromise(() => {
      const pacsCommunication = new (picoxiaDicom().PACSCommunication)();
      return pacsCommunication
        .associate(pacsConfiguration ?? this.pacsConfiguration_)
        .finally(() => pacsCommunication.release());
    });
}
